import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { copy } from '../copy/en-CA.ts';

/**
 * A wide table's own scrolling area (CLAUDE.md §7: a labelled region a
 * keyboard can reach). When the table is wider than the screen, a line above
 * it says so; on a phone nothing else shows that more columns are off to the
 * side. The line appears only while the table really overflows.
 */
export function ScrollRegion({ label, className, style, children }: { label: string; className?: string; style?: CSSProperties; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const check = () => {
      setOverflowing(el.scrollWidth > el.clientWidth + 1);
    };
    check();
    // Both the area and the table inside it: either can change width
    // (a turned phone, a tab shown, the data loading).
    const observer = new ResizeObserver(check);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {overflowing ? (
        <p className="scroll-hint muted small no-print">
          {copy.schedule.scrollHint}{' '}
          <span aria-hidden="true">→</span>
        </p>
      ) : null}
      <div ref={ref} className={`table-region ${className ?? ''}`} role="region" aria-label={label} tabIndex={0} style={style}>
        {children}
      </div>
    </>
  );
}
